import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { removeSecondParent, useParents } from '../api/second-parents.js';
import { RemoveParentSheet } from '../components/remove-parent-sheet.js';
import { day } from '../components/video-format.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * The other parent's page, for the first parent (canvas row D, "Sam's page,
 * from Helen's side", approved 17 October): their email, when they joined, and
 * Remove Sam from the family, which opens the warning panel.
 *
 * An invitation not yet taken opens the same page, with when it was sent:
 * removing it is how a mistyped address is put right, there being one other
 * parent at a time.
 *
 * Only the first parent reaches it from Account; the API refuses the removal
 * to anybody else.
 */
export function OtherParentRoute() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const { state } = useParents();
  const [asking, setAsking] = useState(false);
  const [working, setWorking] = useState(false);
  const [failed, setFailed] = useState(false);

  if (state.status === 'loading') return <WaitNote>Checking…</WaitNote>;

  const other = state.status === 'ready' && state.parents.you === 'holder' ? state.parents.other : null;
  if (!other) {
    return (
      <section className="panel">
        <h2>Not on this account</h2>
        <Link className="primary" to="/account">
          Back to your account
        </Link>
      </section>
    );
  }

  async function remove() {
    if (!other || working) return;
    setWorking(true);
    setFailed(false);
    try {
      await removeSecondParent(getToken, other.id);
      navigate('/account', { replace: true });
    } catch {
      setFailed(true);
      setWorking(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <Link className="back" to="/account" aria-label="Back to your account">
          ‹
        </Link>
        <div>
          <h2>
            {other.first_name} {other.last_name}
          </h2>
          <span className="detail">Other adult</span>
        </div>
      </div>

      <Fact label="Email" value={other.email} />
      {other.joined_at ? (
        <Fact label="Joined" value={day(other.joined_at)} />
      ) : other.sent_at ? (
        <Fact label="Invited" value={day(other.sent_at)} />
      ) : null}

      <button
        type="button"
        className="warn-outline"
        onClick={() => {
          setFailed(false);
          setAsking(true);
        }}
      >
        Remove {other.first_name} from the family
      </button>

      {asking ? (
        <RemoveParentSheet
          first={other.first_name}
          working={working}
          failed={failed}
          onRemove={() => void remove()}
          onKeep={() => setAsking(false)}
        />
      ) : null}
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="fact">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}
